<template>
  <div>
    <CardBase :headerBorder="false">
      <template #header>{{ $route.meta.title }}</template>
      <template #toolbar>
        <div class="flex mt-2 md:mt-0">
          <router-link
            v-if="userCan('create.bonus')"
            :to="{ name: 'BonusImport' }"
            class="
              button
              bg-yellow-400
              hover:bg-yellow-600
              hover:text-white
              poppins
              mr-2
            "
          >
            <font-awesome-icon
              :icon="['fas', 'plus-circle']"
              class="mr-1"
            />Import
          </router-link>
          <router-link
            v-if="userCan('create.bonus')"
            :to="{ name: 'BonusCreate' }"
            class="button button--primary poppins"
          >
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />New
            Bonus
          </router-link>
        </div>
      </template>
      <template #content>
        <form
          class="form form-search"
          method="get"
          @submit.prevent="reloadTable"
        >
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Search</label>
              <input
                type="text"
                class="form__input"
                placeholder="Panel ID / Username"
                v-model="formData.search"
              />
            </div>
            <div class="form-search__double" v-show="archivedNote">
              <label class="form__label">Archived {{ archivedNote }}</label>
              <select class="form__input" v-model="formData.archived">
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Start Date</label>
              <date-picker
                v-model="formData.start_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="Start Date"
                @close="checkEndDate"
                :disabled-date="notAfterToday"
              ></date-picker>
            </div>
            <div class="form-search__double">
              <label class="form__label">End Date</label>
              <date-picker
                v-model="formData.end_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="End Date"
                :disabled-date="notBeforeStart"
              ></date-picker>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Asset</label>
              <select class="form__input" v-model="formData.asset_id">
                <option value="">Show All</option>
                <option v-for="row in listAsset" :key="row.id" :value="row.id">
                  {{ row.name }}
                </option>
              </select>
            </div>
            <div class="form-search__double">
              <label class="form__label">Status</label>
              <select class="form__input" v-model="formData.status">
                <option value="">Show All</option>
                <option value="1">Approved</option>
                <option value="2">Rejected</option>
              </select>
            </div>
          </div>

          <div class="form-search__action">
            <div class="w-6/12 flex">
              <BaseButton type="submit" buttonClass="secondary"
                >Search</BaseButton
              >
              <BaseButton
                type="button"
                class="mx-2"
                buttonClass="default"
                @click="reset"
                >Reset Search</BaseButton
              >
              <BaseButton
                type="button"
                buttonClass="primary"
                @click="exportResults"
                >Export Results</BaseButton
              >
            </div>
          </div>
        </form>

        <vue-good-table
          mode="remote"
          theme="polar-bear"
          :rows="tableData.rows"
          :columns="tableData.columns"
          :total-rows="tableData.totalRows"
          :is-loading.sync="tableData.isLoading"
          :pagination-options="{
            enabled: true,
            dropdownAllowAll: false,
            perPageDropdown: perPageDropdown,
            rowsPerPageLabel: 'Results per page',
          }"
          :sort-options="{
            enabled: false,
          }"
          styleClass="vgt-table striped bordered"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'date'">
              {{ props.row.date }}
            </span>
            <span v-else-if="props.column.field == 'created'">
              {{ props.row.created_by + " / " + props.row.created_at }}
            </span>
            <span v-else-if="props.column.field == 'status'">
              <span
                class="flex items-center text-green-500"
                v-if="props.row.status == 1"
              >
                <font-awesome-icon
                  :icon="['fas', 'check-circle']"
                  class="mr-2"
                  size="lg"
                />Approved
              </span>

              <span
                class="flex items-center text-red-500"
                v-else-if="props.row.status == 2"
              >
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="mr-2"
                  size="lg"
                />Rejected
              </span>
            </span>
            <span v-else-if="props.column.field == 'uuid'">
              <template v-if="props.row.member_transaction_id">
                <router-link
                  v-if="userCan('update.deposit')"
                  :to="{
                    name: 'DepositEdit',
                    params: { id: props.row.member_transaction_id },
                  }"
                  class="
                    w-full
                    button
                    border-gray-600
                    button
                    border border-solid
                    hover:bg-gray-600
                    hover:text-white
                    transition-all
                    duration-200
                    text-center text-gray-600
                  "
                  ><font-awesome-icon
                    class="mr-1"
                    :icon="
                      props.row.status != 2 ? ['fas', 'edit'] : ['far', 'eye']
                    "
                  />
                  <template v-if="props.row.status == 2">View</template
                  ><template v-else>Edit</template></router-link
                >
              </template>
              <template v-else>
                <template v-if="userCan('update.deposit')">
                  <router-link
                    :to="{
                      name: 'BonusEdit',
                      params: {
                        id: props.row.id,
                      },
                    }"
                    v-tooltip="
                      'Edit [' +
                        props.row.member_account.asset.name +
                        '] ' +
                        props.row.member_account.panel_id
                    "
                    class="table-action__link mx-2"
                  >
                    <font-awesome-icon :icon="['fas', 'edit']" />
                  </router-link>
                </template>
              </template>
            </span>
          </template>
        </vue-good-table>
      </template>
    </CardBase>
  </div>
</template>

<script>
  import { showListMixin } from "@/mixins/showListMixin";
  import { datePickerMixin } from "@/mixins/datePickerMixin";
  import { getListAssetMixin } from "@/mixins/getListAssetMixin";
  import DatePicker from "vue2-datepicker";

  export default {
    data() {
      return {
        archivedNote: '',
        exportToken: null,
        listAsset: [],
        tableData: {
          columns: [
            {
              label: "Date",
              field: "date",
            },
            {
              label: "Account ID",
              field: "member_account.fname",
            },
            {
              label: "Bonus",
              field: "amount",
            },
            {
              label: "Rolling",
              field: "rolling",
            },
            {
              label: "Cashback",
              field: "cashback",
            },
            {
              label: "Status",
              field: "status",
            },
            {
              label: "Created",
              field: "created",
            },
            {
              label: "",
              field: "uuid",
              thClass: "vgt-table__action",
              tdClass: "vgt-table__action",
            },
          ],
          sort_by: "date",
          sort_dir: "desc",
        },
        formData: {
          archived: 0,
          status: 1,
          asset_id: "",
          search: "",
          start_date: null,
          end_date: null,
        },
      };
    },
    mixins: [showListMixin, datePickerMixin, getListAssetMixin],
    components: {
      DatePicker,
    },
    created() {
      this.getListAsset();
    },
    methods: {
      exportResults() {
        window.open(
          process.env.VUE_APP_API_URL + "/bonus/export/" + this.exportToken
        );
      },
      reloadTable() {
        if (this.formData.search && this.formData.search.length < 3) {
          this.$swal({
            position: "bottom-end",
            showConfirmButton: false,
            toast: true,
            timer: 3000,
            timerProgressBar: true,
            title: "Search query must have at least 3 characters",
            icon: "warning",
          });
        } else {
          this.tableData.currentPage = 1;
          this.$router
            .replace({
              query: {
                archived: this.formData.archived,
                sort_dir: this.tableData.sort_dir,
                status: this.formData.status,
                asset_id: this.formData.asset_id,
                search: this.formData.search,
                start_date: this.formData.start_date,
                end_date: this.formData.end_date,
                tz: new Date().getTime(),
              },
            })
            .catch(() => {});
        }
      },
      loadTableData() {
        this.tableData.isLoading = true;

        const queryParams = this.$route.query;
        let searchParam = "";

        if (queryParams.search != undefined) {
          this.formData = { ...this.formData, ...this.$route.query };
          this.tableData.sort_dir = this.$route.query.sort_dir;
          for (const key in queryParams) {
            if (key != "sort_dir") {
              searchParam += `&${key}=${queryParams[key]}`;
            }
          }
        } else {
          searchParam += `&start_date=${this.formData.start_date}&end_date=${this.formData.end_date}&archived=${this.formData.archived}`;
        }

        this.$http
          .get(
            process.env.VUE_APP_API_URL +
              `${this.$route.path}?page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
          )
          .then((res) => {
            this.tableData.rows = res.data.data;
            this.tableData.totalRows = res.data.total;
            this.exportToken = res.data.export_token;
            this.archivedNote = res.data.archived;
          });
      },
      dateFormat(date) {
        return this.moment(date).format("DD MMM YYYY - HH:mm");
      },
      reset() {
        this.formData.search = "";
        this.formData.start_date = this.moment(new Date()).format("YYYY-MM-DD");
        this.formData.end_date = this.moment(new Date()).format("YYYY-MM-DD");
        this.formData.status = "1";
        this.formData.asset = null;
        this.formData.asset_id = "";
        this.tableData.sort_dir = "desc";
        this.tableData.limit = 10;

        this.$router.push({ name: "BonusList" }).catch(() => {});
      },
    },
  };
</script>
